import { Injectable, computed, signal } from '@angular/core';
import {
  FLAGSHIP_PROJECTS,
  FlagshipProject,
  FrameworkDefinition,
  FrameworkKey,
  FrameworkProcess,
  ImplementationReport,
  Indicator,
  Intervention,
  InterventionReport,
  NATIONAL_FRAMEWORKS,
  PLAN_INSTITUTIONS,
  ProjectActivity,
  RegisterItem,
  SiteEvidence,
  StepStatus,
  Target,
  averageProgress,
  indicatorProgress,
  seedStepStatus,
} from './framework-catalog';

/** Headline figure for one framework, as shown on its card. */
export interface FrameworkMetric {
  label: string;
  progress: number | null;
  progressLabel: string;
}

export interface IndicatorRow {
  framework: FrameworkDefinition;
  area: FrameworkProcess;
  indicator: Indicator;
  progress: number | null;
}

/**
 * In-memory state for the frameworks prototype. Edits made on the framework pages
 * (indicators, step statuses, project progress, convention reports) live here for the
 * session and are lost on reload — nothing is sent to the API yet.
 */
@Injectable({ providedIn: 'root' })
export class FrameworkPrototypeStore {
  readonly frameworks = signal<FrameworkDefinition[]>(structuredClone(NATIONAL_FRAMEWORKS));
  readonly projects = signal<FlagshipProject[]>(structuredClone(FLAGSHIP_PROJECTS));
  readonly institutions = PLAN_INSTITUTIONS;

  /** Keyed `${stepKey}:${institutionId}` */
  readonly stepStatuses = signal<Record<string, StepStatus>>(this.seedStatuses());

  /** Every indicator under an area process, across all frameworks. */
  readonly indicatorRows = computed<IndicatorRow[]>(() =>
    this.frameworks().flatMap((framework) =>
      framework.processes
        .filter((p) => p.kind === 'area')
        .flatMap((area) =>
          (area.indicators ?? []).map((indicator) => ({
            framework,
            area,
            indicator,
            progress: indicatorProgress(indicator),
          })),
        ),
    ),
  );

  readonly metrics = computed(() => {
    const map = new Map<FrameworkKey, FrameworkMetric>();
    for (const f of this.frameworks()) map.set(f.key, this.metricFor(f));
    return map;
  });

  framework(key: string | null | undefined): FrameworkDefinition | undefined {
    return this.frameworks().find((f) => f.key === key);
  }

  process(frameworkKey: string | null | undefined, processKey: string | null | undefined) {
    return this.framework(frameworkKey)?.processes.find((p) => p.key === processKey);
  }

  stepStatus(stepKey: string, institutionId: string): StepStatus {
    return this.stepStatuses()[`${stepKey}:${institutionId}`] ?? 'NOT_STARTED';
  }

  setStepStatus(stepKey: string, institutionId: string, status: StepStatus): void {
    this.stepStatuses.update((all) => ({ ...all, [`${stepKey}:${institutionId}`]: status }));
  }

  saveIndicator(frameworkKey: string, processKey: string, indicator: Indicator): void {
    this.updateProcess(frameworkKey, processKey, (p) => {
      const list = p.indicators ?? [];
      const exists = list.some((i) => i.id === indicator.id);
      return {
        ...p,
        indicators: exists
          ? list.map((i) => (i.id === indicator.id ? indicator : i))
          : [...list, indicator],
      };
    });
  }

  removeIndicator(frameworkKey: string, processKey: string, indicatorId: string): void {
    this.updateProcess(frameworkKey, processKey, (p) => ({
      ...p,
      indicators: (p.indicators ?? []).filter((i) => i.id !== indicatorId),
    }));
  }

  indicator(frameworkKey: string, processKey: string, indicatorId: string): Indicator | undefined {
    return this.process(frameworkKey, processKey)?.indicators?.find((i) => i.id === indicatorId);
  }

  target(
    frameworkKey: string,
    processKey: string,
    indicatorId: string,
    targetId: string,
  ): Target | undefined {
    return this.indicator(frameworkKey, processKey, indicatorId)?.targets?.find(
      (t) => t.id === targetId,
    );
  }

  saveTarget(frameworkKey: string, processKey: string, indicatorId: string, target: Target): void {
    this.updateIndicator(frameworkKey, processKey, indicatorId, (i) => {
      const list = i.targets ?? [];
      const exists = list.some((t) => t.id === target.id);
      return {
        ...i,
        targets: exists ? list.map((t) => (t.id === target.id ? target : t)) : [...list, target],
      };
    });
  }

  removeTarget(frameworkKey: string, processKey: string, indicatorId: string, targetId: string): void {
    this.updateIndicator(frameworkKey, processKey, indicatorId, (i) => ({
      ...i,
      targets: (i.targets ?? []).filter((t) => t.id !== targetId),
    }));
  }

  /** Adds or replaces a report; a new report also moves the target's progress. */
  saveReport(
    frameworkKey: string,
    processKey: string,
    indicatorId: string,
    targetId: string,
    report: ImplementationReport,
  ): void {
    this.updateIndicator(frameworkKey, processKey, indicatorId, (i) => ({
      ...i,
      targets: (i.targets ?? []).map((t) => {
        if (t.id !== targetId) return t;
        const exists = t.reports.some((r) => r.id === report.id);
        return {
          ...t,
          progress: exists ? t.progress : report.progress,
          reports: exists
            ? t.reports.map((r) => (r.id === report.id ? report : r))
            : [...t.reports, report],
        };
      }),
    }));
  }

  intervention(frameworkKey: string, processKey: string, id: string): Intervention | undefined {
    return this.process(frameworkKey, processKey)?.interventions?.find((i) => i.id === id);
  }

  /** Adds a report; a progress report also moves the matrix's financial and physical progress. */
  addInterventionReport(
    frameworkKey: string,
    processKey: string,
    id: string,
    report: InterventionReport,
  ): void {
    this.updateProcess(frameworkKey, processKey, (p) => ({
      ...p,
      interventions: (p.interventions ?? []).map((i) => {
        if (i.id !== id) return i;
        const progress =
          report.kind === 'PROGRESS'
            ? { financial: report.financial ?? i.financial, physical: report.physical ?? i.physical }
            : {};
        return { ...i, ...progress, reports: [...(i.reports ?? []), report] };
      }),
    }));
  }

  updateRegisterItem(
    frameworkKey: string,
    processKey: string,
    id: string,
    patch: Partial<RegisterItem>,
  ): void {
    this.updateProcess(frameworkKey, processKey, (p) => ({
      ...p,
      items: (p.items ?? []).map((i) => (i.id === id ? { ...i, ...patch } : i)),
    }));
  }

  updateProject(id: string, patch: Partial<FlagshipProject>): void {
    this.projects.update((all) => all.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }

  project(id: string | null | undefined): FlagshipProject | undefined {
    return this.projects().find((p) => p.id === id);
  }

  saveActivity(projectId: string, activity: ProjectActivity): void {
    this.updateProjectWith(projectId, (p) => {
      const exists = p.activities.some((a) => a.id === activity.id);
      return {
        ...p,
        activities: exists
          ? p.activities.map((a) => (a.id === activity.id ? activity : a))
          : [...p.activities, activity],
      };
    });
  }

  removeActivity(projectId: string, activityId: string): void {
    this.updateProjectWith(projectId, (p) => ({
      ...p,
      activities: p.activities.filter((a) => a.id !== activityId),
      evidence: p.evidence.map((e) => (e.activityId === activityId ? { ...e, activityId: null } : e)),
    }));
  }

  addEvidence(projectId: string, evidence: SiteEvidence): void {
    this.updateProjectWith(projectId, (p) => ({ ...p, evidence: [evidence, ...p.evidence] }));
  }

  private updateProjectWith(id: string, change: (p: FlagshipProject) => FlagshipProject): void {
    this.projects.update((all) => all.map((p) => (p.id === id ? change(p) : p)));
  }

  private metricFor(framework: FrameworkDefinition): FrameworkMetric {
    if (framework.key === 'flagship') {
      const projects = this.projects();
      const progress = projects.length
        ? Math.round(projects.reduce((s, p) => s + p.physical, 0) / projects.length)
        : null;
      return { label: `${projects.length} projects`, progress, progressLabel: 'avg physical progress' };
    }

    if (framework.key === 'strategic-plans') {
      const statuses = Object.values(this.stepStatuses());
      const complete = statuses.filter((s) => s === 'COMPLETE').length;
      return {
        label: `${this.institutions.length} institutions`,
        progress: statuses.length ? Math.round((complete / statuses.length) * 100) : null,
        progressLabel: 'of M&E cycle steps complete',
      };
    }

    const indicators = framework.processes.flatMap((p) => p.indicators ?? []);
    return {
      label: `${indicators.length} indicators`,
      progress: averageProgress(indicators),
      progressLabel: 'avg progress to target',
    };
  }

  private updateIndicator(
    frameworkKey: string,
    processKey: string,
    indicatorId: string,
    change: (indicator: Indicator) => Indicator,
  ): void {
    this.updateProcess(frameworkKey, processKey, (p) => ({
      ...p,
      indicators: (p.indicators ?? []).map((i) => (i.id === indicatorId ? change(i) : i)),
    }));
  }

  private updateProcess(
    frameworkKey: string,
    processKey: string,
    change: (process: FrameworkProcess) => FrameworkProcess,
  ): void {
    this.frameworks.update((all) =>
      all.map((f) =>
        f.key === frameworkKey
          ? { ...f, processes: f.processes.map((p) => (p.key === processKey ? change(p) : p)) }
          : f,
      ),
    );
  }

  private seedStatuses(): Record<string, StepStatus> {
    const plans = NATIONAL_FRAMEWORKS.find((f) => f.key === 'strategic-plans');
    const steps = (plans?.processes ?? []).filter((p) => p.step);
    const statuses: Record<string, StepStatus> = {};
    steps.forEach((step, stepIndex) =>
      PLAN_INSTITUTIONS.forEach((inst, instIndex) => {
        statuses[`${step.key}:${inst.id}`] = seedStepStatus(stepIndex, instIndex);
      }),
    );
    return statuses;
  }
}
